import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Smartphone, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field, Input, Switch } from '@/components/ui/form';
import { Card, PageHeader, Spinner } from '@/components/ui/layout';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { locale, t, tn } from '@/lib/i18n';
import {
  type AppCheckMode,
  type AppSettings,
  type AppUsage,
  useAppSettings,
  useAppUsage,
  useSaveAppSettings,
} from '@/lib/mobile-app';

const MODES: { mode: AppCheckMode; label: string; help: string }[] = [
  { mode: 'off', label: t('Off'), help: t('Tokens are not checked.') },
  {
    mode: 'monitor',
    label: t('Monitor'),
    help: t(
      'Tokens are checked and counted, nothing is blocked. Use this until almost all requests are valid.',
    ),
  },
  {
    mode: 'enforce',
    label: t('Enforce'),
    help: t('Requests without a valid token are refused: only the real app can use API v2.'),
  },
];

/** The new mobile app (API v2): usage, App Check and the settings the app reads at start. */
export function MobileAppPage() {
  const { can } = useAuth();
  return (
    <>
      <PageHeader
        title={t('Mobile app')}
        subtitle={t('The new app (API v2): who uses it, App Check, and what the app reads when it starts.')}
      />
      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <UsageCard />
        {can('admin') && <SettingsForm />}
        {can('admin') && <ArrivalTimesCard />}
        <StationArrivalsCard editable={can('admin')} />
      </div>
    </>
  );
}

interface EtaSettings {
  mode: 'history' | 'speed';
  computed_at: string | null;
  segments: number;
  directions: number;
  events: number;
}

const ETA_MODES: { mode: EtaSettings['mode']; label: string; help: string }[] = [
  {
    mode: 'history',
    label: t('From history'),
    help: t(
      'How long buses usually take between stations at this time of day, adjusted to how the bus runs today. Where history is missing, the current speed is used.',
    ),
  },
  {
    mode: 'speed',
    label: t('From current speed (old)'),
    help: t('Distance divided by the bus’s average speed of the last 15 minutes, as before.'),
  },
];

/** How the app's arrival minutes are computed (both apps); switching applies within a minute. */
function ArrivalTimesCard() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['settings', 'eta'],
    queryFn: () => api<{ settings: EtaSettings }>('/settings/eta'),
  });
  const save = useMutation({
    mutationFn: (mode: EtaSettings['mode']) => api('/settings/eta', { method: 'PUT', body: { mode } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'eta'] });
      toast.success(t('Saved'));
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('Could not save')),
  });
  const s = q.data?.settings;
  return (
    <Card title={t('Arrival times')}>
      {!s ? (
        <Spinner />
      ) : (
        <div className="space-y-3 text-sm">
          <div className="grid gap-2 sm:grid-cols-2">
            {ETA_MODES.map((m) => (
              <label
                key={m.mode}
                className={`cursor-pointer rounded-md border p-2.5 ${s.mode === m.mode ? 'border-brand-600 bg-brand-50' : 'border-slate-200 hover:border-slate-300'}`}
              >
                <input
                  type="radio"
                  name="eta-mode"
                  className="sr-only"
                  checked={s.mode === m.mode}
                  disabled={save.isPending}
                  onChange={() => save.mutate(m.mode)}
                />
                <div className="font-medium text-slate-900">{m.label}</div>
                <div className="mt-0.5 text-xs text-slate-500">{m.help}</div>
              </label>
            ))}
          </div>
          <p className="text-xs text-slate-500">
            {s.computed_at
              ? `${t('History updated {time}', { time: new Date(s.computed_at).toLocaleString(locale) })} · ${tn(s.directions, '{n} direction', '{n} directions')} · ${tn(s.events, '{n} station passing', '{n} station passings')}`
              : t('History is being built: a few minutes after the update, then every night.')}
          </p>
          <p className="text-xs text-slate-500">
            {t('Applies to both apps within a minute; the response format does not change.')}
          </p>
        </div>
      )}
    </Card>
  );
}

interface ArrivalSettings {
  show_scheduled: boolean;
  scheduled_within_min: number;
  scheduled_per_direction: number;
}

/** What a station's list shows besides the buses on their way (both apps' v2 screens, within 30 s). */
function StationArrivalsCard({ editable }: { editable: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['settings', 'arrivals'],
    queryFn: () => api<{ settings: ArrivalSettings }>('/settings/arrivals'),
  });
  const [form, setForm] = useState<ArrivalSettings | null>(null);
  useEffect(() => {
    if (q.data && !form) setForm(q.data.settings);
  }, [q.data, form]);
  const save = useMutation({
    mutationFn: (s: ArrivalSettings) => api('/settings/arrivals', { method: 'PUT', body: s }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'arrivals'] });
      toast.success(t('Saved'));
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('Could not save')),
  });
  return (
    <Card title={t('Arrivals at stations')}>
      {!form ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          <Switch
            checked={form.show_scheduled}
            onChange={(v) => editable && setForm({ ...form, show_scheduled: v })}
            label={t('Show departures from the timetable when no bus is on its way')}
          />
          <p className="text-xs text-slate-500">
            {form.show_scheduled
              ? t('Marked “scheduled” in the list, between the live buses by time.')
              : t(
                  'Off: a station lists live buses only; with none on its way it says no line is expected soon.',
                )}
          </p>
          {form.show_scheduled && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('How far ahead')} hint={t('Departures later than this are not listed.')}>
                {(id) => (
                  <div className="flex items-center gap-2">
                    <Input
                      id={id}
                      type="number"
                      className="w-24"
                      disabled={!editable}
                      value={String(form.scheduled_within_min)}
                      onChange={(e) => setForm({ ...form, scheduled_within_min: Number(e.target.value) })}
                    />
                    <span className="text-slate-500">min</span>
                  </div>
                )}
              </Field>
              <Field label={t('Per direction')} hint={t('How many departures of each direction.')}>
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    className="w-24"
                    disabled={!editable}
                    value={String(form.scheduled_per_direction)}
                    onChange={(e) => setForm({ ...form, scheduled_per_direction: Number(e.target.value) })}
                  />
                )}
              </Field>
            </div>
          )}
          {editable && (
            <Button loading={save.isPending} onClick={() => save.mutate(form)}>
              {t('Save')}
            </Button>
          )}
          <p className="text-xs text-slate-500">
            {t(
              'Applies to the new app within 30 seconds, no update needed. Trip planning still uses the timetable.',
            )}
          </p>
        </div>
      )}
    </Card>
  );
}

const sum = (rows: AppUsage['hours'], k: keyof Omit<AppUsage['hours'][number], 'hour'>) =>
  rows.reduce((a, r) => a + r[k], 0);
const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '—');

function UsageCard() {
  const q = useAppUsage();
  if (!q.data) return <Spinner />;
  const { hours, installs, versions } = q.data;
  const v1 = sum(hours, 'v1');
  const v2 = sum(hours, 'v2');
  const checked = sum(hours, 'valid') + sum(hours, 'missing') + sum(hours, 'invalid') + sum(hours, 'reused');
  const max = Math.max(1, ...hours.map((h) => h.v1 + h.v2));
  const n = (x: number) => x.toLocaleString(locale);
  return (
    <Card title={t('Last 24 hours')}>
      <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
        <Stat label={t('Old app (v1)')} value={n(v1)} sub={pct(v1, v1 + v2)} />
        <Stat label={t('New app (v2)')} value={n(v2)} sub={pct(v2, v1 + v2)} />
        <Stat
          label={t('Valid App Check')}
          value={pct(sum(hours, 'valid'), checked)}
          sub={n(sum(hours, 'valid'))}
        />
        <Stat
          label={t('Missing or invalid')}
          value={n(sum(hours, 'missing') + sum(hours, 'invalid') + sum(hours, 'reused'))}
          sub={t('{missing} missing, {invalid} invalid', {
            missing: sum(hours, 'missing'),
            invalid: sum(hours, 'invalid') + sum(hours, 'reused'),
          })}
        />
      </div>
      <div className="mt-4">
        <div className="mb-1.5 flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <span className="inline-block size-2.5 rounded-sm bg-slate-400" /> v1
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block size-2.5 rounded-sm bg-brand-600" /> v2
          </span>
          <span className="ml-auto">{t('Requests per hour')}</span>
        </div>
        <div className="flex h-24 items-end gap-[2px] border-b border-slate-200">
          {hours.map((h) => (
            <div
              key={h.hour}
              className="flex flex-1 flex-col justify-end"
              title={`${new Date(h.hour).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}: v1 ${h.v1}, v2 ${h.v2}`}
            >
              <div className="bg-brand-600" style={{ height: `${(h.v2 / max) * 96}px` }} />
              <div className="bg-slate-400" style={{ height: `${(h.v1 / max) * 96}px` }} />
            </div>
          ))}
        </div>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <div className="mb-1 text-sm font-medium text-slate-700">{t('Installs of the new app')}</div>
          {installs.length === 0 ? (
            <p className="text-sm text-slate-500">{t('None yet.')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-500">
                <tr>
                  <th className="py-1 font-normal" />
                  <th className="py-1 font-normal">{t('Total')}</th>
                  <th className="py-1 font-normal">{t('Active (7 days)')}</th>
                  <th className="py-1 font-normal">{t('Push')}</th>
                </tr>
              </thead>
              <tbody>
                {installs.map((i) => (
                  <tr key={i.platform} className="border-t border-slate-100">
                    <td className="py-1 capitalize">{i.platform === 'ios' ? 'iOS' : 'Android'}</td>
                    <td className="py-1">{n(i.total)}</td>
                    <td className="py-1">{n(i.active_7d)}</td>
                    <td className="py-1">{n(i.push)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div>
          <div className="mb-1 text-sm font-medium text-slate-700">{t('Versions (7 days)')}</div>
          {versions.length === 0 ? (
            <p className="text-sm text-slate-500">{t('None yet.')}</p>
          ) : (
            <ul className="text-sm">
              {versions.map((v) => (
                <li
                  key={`${v.platform}-${v.version}`}
                  className="flex justify-between border-t border-slate-100 py-1"
                >
                  <span>
                    {v.platform === 'ios' ? 'iOS' : 'Android'} {v.version}
                  </span>
                  <span className="text-slate-500">{n(v.n)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function SettingsForm() {
  const q = useAppSettings(true);
  const save = useSaveAppSettings();
  const [s, setS] = useState<AppSettings | null>(null);
  /** Stable React keys for the ticket rows (their ids can be edited). */
  const [keys, setKeys] = useState<string[]>([]);
  useEffect(() => {
    if (q.data && !s) {
      setS(q.data.settings);
      setKeys(q.data.settings.tickets.map(() => crypto.randomUUID()));
    }
  }, [q.data, s]);
  if (!s) return <Spinner />;
  const set = (p: Partial<AppSettings>) => setS({ ...s, ...p });
  const platforms = [
    ['android', 'Android'],
    ['ios', 'iOS'],
  ] as const;

  return (
    <Card title={t('Settings the app reads')}>
      <div className="space-y-5 text-sm">
        <section>
          <div className="mb-1.5 font-medium text-slate-700">App Check</div>
          <div className="grid gap-2 sm:grid-cols-3">
            {MODES.map((m) => (
              <label
                key={m.mode}
                className={`cursor-pointer rounded-md border p-2.5 ${s.app_check_mode === m.mode ? 'border-brand-600 bg-brand-50' : 'border-slate-200 hover:border-slate-300'}`}
              >
                <input
                  type="radio"
                  name="app-check"
                  className="sr-only"
                  checked={s.app_check_mode === m.mode}
                  onChange={() => set({ app_check_mode: m.mode })}
                />
                <div className="font-medium text-slate-900">{m.label}</div>
                <div className="mt-0.5 text-xs text-slate-500">{m.help}</div>
              </label>
            ))}
          </div>
        </section>

        <section className="grid gap-3 sm:grid-cols-2">
          {platforms.map(([p, name]) => (
            <div key={p} className="space-y-2">
              <div className="flex items-center gap-1.5 font-medium text-slate-700">
                <Smartphone className="size-4" /> {name}
              </div>
              <Field label={t('Minimum version')} hint={t('Older versions must update.')}>
                {(id) => (
                  <Input
                    id={id}
                    value={s.min_version[p]}
                    onChange={(e) => set({ min_version: { ...s.min_version, [p]: e.target.value.trim() } })}
                  />
                )}
              </Field>
              <Field label={t('Latest version')} hint={t('Older versions see an update hint.')}>
                {(id) => (
                  <Input
                    id={id}
                    value={s.latest_version[p]}
                    onChange={(e) =>
                      set({ latest_version: { ...s.latest_version, [p]: e.target.value.trim() } })
                    }
                  />
                )}
              </Field>
              <Field label={t('Store link')}>
                {(id) => (
                  <Input
                    id={id}
                    value={s.store_url[p]}
                    onChange={(e) => set({ store_url: { ...s.store_url, [p]: e.target.value.trim() } })}
                  />
                )}
              </Field>
            </div>
          ))}
        </section>

        <section>
          <div className="mb-1 font-medium text-slate-700">{t('SMS tickets')}</div>
          <p className="mb-2 text-xs text-slate-500">
            {t('The SMS is sent to the number with the text. {line} is replaced with the chosen line.')}
          </p>
          <ul className="space-y-2">
            {s.tickets.map((tk, i) => {
              const setTicket = (p: Partial<typeof tk>) =>
                set({ tickets: s.tickets.map((x, j) => (j === i ? { ...x, ...p } : x)) });
              return (
                <li key={keys[i]} className="flex flex-wrap items-center gap-2">
                  <Input
                    className="h-8 w-28"
                    value={tk.id}
                    onChange={(e) => setTicket({ id: e.target.value.trim() })}
                    aria-label={t('Name')}
                  />
                  <Input
                    className="h-8 w-24"
                    value={tk.sms_to}
                    onChange={(e) => setTicket({ sms_to: e.target.value.trim() })}
                    aria-label={t('Number')}
                  />
                  <Input
                    className="h-8 min-w-0 flex-1"
                    value={tk.body}
                    onChange={(e) => setTicket({ body: e.target.value })}
                    aria-label={t('SMS text')}
                  />
                  <label className="flex items-center gap-1 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={tk.needs_line}
                      onChange={(e) => setTicket({ needs_line: e.target.checked })}
                      className="rounded border-slate-300"
                    />
                    {t('Choose a line')}
                  </label>
                  <button
                    type="button"
                    className="text-slate-400 hover:text-red-600"
                    onClick={() => {
                      set({ tickets: s.tickets.filter((_, j) => j !== i) });
                      setKeys(keys.filter((_, j) => j !== i));
                    }}
                    aria-label={t('Remove')}
                  >
                    <Trash2 className="size-4" />
                  </button>
                </li>
              );
            })}
          </ul>
          <Button
            variant="secondary"
            className="mt-2"
            icon={<Plus className="size-4" />}
            onClick={() =>
              set({
                tickets: [
                  ...s.tickets,
                  { id: `ticket${s.tickets.length + 1}`, sms_to: '', body: '', needs_line: false },
                ],
              })
            }
          >
            {t('Add ticket')}
          </Button>
        </section>

        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {(['phone', 'viber', 'email'] as const).map((k) => (
            <Field key={k} label={k === 'phone' ? t('Phone') : k === 'viber' ? 'Viber' : t('E-mail')}>
              {(id) => (
                <Input
                  id={id}
                  value={s.contact[k]}
                  onChange={(e) => set({ contact: { ...s.contact, [k]: e.target.value.trim() } })}
                />
              )}
            </Field>
          ))}
          <Field label={t('Call hours')} hint={t('Shown in the app as written, e.g. 05:30 - 23:30')}>
            {(id) => (
              <Input
                id={id}
                value={s.contact.hours ?? ''}
                placeholder="05:30 - 23:30"
                onChange={(e) => set({ contact: { ...s.contact, hours: e.target.value } })}
              />
            )}
          </Field>
        </section>

        <section className="flex flex-wrap gap-6">
          <Switch
            checked={s.features.station_ads}
            onChange={(v) => set({ features: { ...s.features, station_ads: v } })}
            label={t('Station ads')}
          />
          <Switch
            checked={s.features.reports}
            onChange={(v) => set({ features: { ...s.features, reports: v } })}
            label={t('Reports')}
          />
        </section>

        <div className="border-t border-slate-200 pt-4">
          <Button loading={save.isPending} onClick={() => save.mutate(s)}>
            {t('Save')}
          </Button>
        </div>
      </div>
    </Card>
  );
}
